import { useState, type ReactNode } from "react";
import { CODING_TOPICS, type TopicId } from "../../data/prep/topics";
import type { PrepLevel } from "../../data/prep/roleProfiles";
import type { Rating, Weekday } from "../../utils/prepSkills";
import { MINUTE_OPTIONS, WEEK, formatMinutes, type PrepSettingsDoc } from "../../utils/prep";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ChevronDown } from "lucide-react";
import type { PrepEvent } from "../../utils/prepPlanner";
import { cn } from "../../lib/utils";

// Interview Prep setup, a card over a faded week: the time they have each day,
// how confident they are in each topic, then (when there are any) the OAs on
// their cards. Shown before the page the first time; after that the settings
// button opens PrepSettings.

const RATINGS: { value: Rating; label: string }[] = [
  { value: "shaky", label: "Shaky" },
  { value: "okay", label: "Okay" },
  { value: "solid", label: "Solid" },
];

const LEVELS: { value: PrepLevel; label: string }[] = [
  { value: "intern", label: "Internships" },
  { value: "newgrad", label: "New grad" },
  { value: "mid", label: "Mid-level" },
  { value: "senior", label: "Senior" },
];
const GROUPS = ["Basics", "Trees & graphs", "Techniques"] as const;

/** The times a day can have: the same steps as the settings page */
const TIMES = MINUTE_OPTIONS;

const DAY_LETTER: Record<Weekday, string> = { mon: "M", tue: "T", wed: "W", thu: "T", fri: "F", sat: "S", sun: "S" };
const DAY_NAME: Record<Weekday, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

type Answers = Omit<PrepSettingsDoc, "completedAt">;

/** "2h 30m", "45m", "5h" */
const hoursMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m}m`;
};

/** "a week, weekends off", "a week, every day", "a week over 4 days" */
function weekShape(dayMinutes: Answers["dayMinutes"]): string {
  const on = WEEK.filter((d) => dayMinutes[d] > 0);
  if (on.length === 7) return "a week, every day";
  if (on.length === 5 && !dayMinutes.sat && !dayMinutes.sun) return "a week, weekends off";
  return `a week over ${on.length} ${on.length === 1 ? "day" : "days"}`;
}

/** "Fri, Oct 23" */
const dayLabel = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const LABEL = "font-plex text-[10.5px] font-medium uppercase tracking-[.12em] text-melt-text3";

/** A row of options in a tinted tray; the picked one is a white tile */
function Segmented<T extends string | number | boolean>({
  options,
  value,
  onChange,
  label,
  size = "sm",
}: {
  options: { value: T; label: ReactNode }[];
  /** null: none picked */
  value: T | null;
  onChange: (value: T) => void;
  label: string;
  size?: "sm" | "lg";
}) {
  return (
    <div className="flex rounded-[12px] bg-[#F3EEE7] p-1" role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(option.value)}
            className={cn(
              "min-w-0 flex-1 rounded-[9px] text-melt-text2 transition-[background-color,box-shadow,color] duration-150 hover:text-melt-text1",
              size === "lg" ? "py-2.5" : "px-2 py-1.5 text-[12.5px] font-medium",
              on && "bg-white text-melt-text1 shadow-[0_1px_3px_rgba(92,52,23,.12)]",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** One day's tile: its letter and time; tap it for a menu of times */
function DayTime({ day, minutes, onChange }: { day: Weekday; minutes: number; onChange: (minutes: number) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={`${DAY_NAME[day]}: ${minutes ? hoursMinutes(minutes) : "off"}`}
        className={cn(
          "flex h-[64px] flex-col items-center justify-center gap-1.5 rounded-[12px] outline-none transition-[background-color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-[#D08A3E] data-[state=open]:ring-2 data-[state=open]:ring-[#D08A3E] data-[state=open]:ring-offset-2",
          minutes ? "bg-[#ECE2D4] text-melt-text1 hover:bg-[#E4D7C5]" : "border border-melt-hair bg-white text-melt-text3 hover:text-melt-text1",
        )}
      >
        <span className="font-plex text-[13px] font-medium leading-none">{DAY_LETTER[day]}</span>
        <span
          className={cn("flex items-center gap-0.5 font-plex text-[10.5px] leading-none", minutes ? "text-melt-text2" : "text-melt-text3")}
        >
          {minutes ? hoursMinutes(minutes) : "Off"}
          <ChevronDown size={10} strokeWidth={2} aria-hidden="true" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="center"
        sideOffset={6}
        avoidCollisions={false}
        className="w-[176px] border-melt-hair bg-melt-menu p-1.5 font-grotesk text-melt-text1 shadow-[0_20px_40px_-20px_rgba(92,52,23,.35)]"
      >
        <p className="px-2 pb-1.5 pt-0.5 font-plex text-[10px] uppercase tracking-[.1em] text-melt-text3">{DAY_NAME[day]}</p>
        <div className="grid grid-cols-2 gap-0.5">
          {TIMES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className={cn(
                "rounded-[7px] px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-melt-text1/[.05]",
                option === minutes ? "bg-melt-peach font-semibold text-melt-text1" : "text-melt-text2",
              )}
            >
              {option ? formatMinutes(option) : "Off"}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** The faded week behind the card */
function GhostWeek() {
  return (
    <div className="pointer-events-none absolute inset-0 select-none opacity-60 blur-[1.5px]" aria-hidden="true">
      <div className="grid grid-cols-7 gap-3">
        {WEEK.map((day, i) => (
          <div key={day} className="flex flex-col gap-3">
            <div className="h-11 rounded-[12px] bg-melt-text1/[.04]" />
            <div className="h-14 rounded-[12px] bg-melt-text1/[.035]" />
            {i % 3 === 0 && <div className="h-10 rounded-[12px] bg-melt-text1/[.03]" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PrepSetup({
  initial,
  softwareRoles,
  oas = [],
  onSave,
}: {
  initial: Answers;
  /** Their onboarding roles include a software role */
  softwareRoles: boolean;
  /** OAs found on their cards: a third step asks which to prep for */
  oas?: PrepEvent[];
  /** `selfPrep`: the OAs they'll prep for themselves */
  onSave: (answers: Answers, selfPrep: string[]) => Promise<void>;
}) {
  const [step, setStep] = useState(0);
  const [selfPrep, setSelfPrep] = useState<Set<string>>(new Set());
  const steps = oas.length ? 3 : 2;
  const [answers, setAnswers] = useState<Answers>(initial);
  const [saving, setSaving] = useState(false);
  const set = (patch: Partial<Answers>) => setAnswers((a) => ({ ...a, ...patch }));
  const rate = (id: TopicId, rating: Rating) => set({ ratings: { ...answers.ratings, [id]: rating } });
  const total = WEEK.reduce((sum, d) => sum + answers.dayMinutes[d], 0);
  const setDay = (day: Weekday, minutes: number) => set({ dayMinutes: { ...answers.dayMinutes, [day]: minutes } });

  const save = async () => {
    setSaving(true);
    try {
      await onSave(answers, Array.from(selfPrep));
    } finally {
      setSaving(false);
    }
  };

  const last = step === steps - 1;

  return (
    <div className="relative min-h-[640px] w-full pb-8 font-grotesk text-melt-text1">
      <GhostWeek />
      <div className="relative mx-auto mt-2 flex max-h-[calc(100vh-140px)] w-full max-w-[600px] flex-col rounded-[22px] bg-white px-7 pb-6 pt-7 shadow-[0_30px_70px_-30px_rgba(92,52,23,.35),0_0_0_1px_rgba(92,52,23,.05)] duration-300 animate-in fade-in zoom-in-[.98]">
        <div className="flex gap-1.5" aria-label={`Step ${step + 1} of ${steps}`}>
          {Array.from({ length: steps }, (_, i) => (
            <i
              key={i}
              className={cn("h-[3px] flex-1 rounded-full transition-colors duration-300", i <= step ? "bg-[#D08A3E]" : "bg-[#EFE8DF]")}
            />
          ))}
        </div>

        <div key={step} className="mt-6 flex min-h-0 flex-1 flex-col duration-300 animate-in fade-in slide-in-from-bottom-1">
          {step === 0 ? (
            <>
              <h1 className="text-[26px] font-semibold leading-[1.15] tracking-[-.02em]">How much time do you have?</h1>
              <p className="mt-2 text-[14.5px] leading-[1.5] text-melt-text2">
                Your plan fits into this. Change it any time, and Caramel learns how long problems take you.
              </p>

              <p className={cn(LABEL, "mt-6 mb-2")}>Each day</p>
              <div className="grid grid-cols-7 gap-2">
                {WEEK.map((day) => (
                  <DayTime key={day} day={day} minutes={answers.dayMinutes[day]} onChange={(minutes) => setDay(day, minutes)} />
                ))}
              </div>

              <p className="mt-6 flex items-baseline gap-2.5">
                {total ? (
                  <>
                    <span className="text-[24px] font-semibold tracking-[-.02em]">{hoursMinutes(total)}</span>
                    <span className="text-[14px] text-melt-text2">{weekShape(answers.dayMinutes)}</span>
                  </>
                ) : (
                  <span className="text-[14px] text-melt-text3">Pick at least one day</span>
                )}
              </p>
            </>
          ) : step === 1 ? (
            <>
              <h1 className="text-[26px] font-semibold leading-[1.15] tracking-[-.02em]">How confident are you?</h1>
              <p className="mt-2 text-[14.5px] leading-[1.5] text-melt-text2">
                Everything starts at okay. Change the ones that aren’t, and your plan starts on your weak spots.
              </p>
              {!softwareRoles && (
                <p className="mt-3 rounded-xl bg-melt-peach px-3.5 py-2.5 text-[13px] leading-[1.5] text-melt-text2">
                  Interview Prep covers coding interviews for now. Prep for other roles is coming.
                </p>
              )}

              <div className="scrollbar-thin -mr-3 mt-5 min-h-0 flex-1 overflow-y-auto pr-3">
                <p className={cn(LABEL, "mb-2")}>Interviewing for</p>
                <Segmented label="Interviewing for" value={answers.level} onChange={(level) => set({ level })} options={LEVELS} />

                <p className={cn(LABEL, "mt-5 mb-2")}>LeetCode Premium</p>
                <Segmented
                  label="LeetCode Premium"
                  value={answers.leetcodePremium}
                  onChange={(leetcodePremium) => set({ leetcodePremium })}
                  options={[
                    { value: true, label: "I have it" },
                    { value: false, label: "I don’t" },
                  ]}
                />

                {GROUPS.map((group) => (
                  <div key={group} className="mt-5">
                    <p className={LABEL}>{group}</p>
                    <div className="mt-1">
                      {CODING_TOPICS.filter((t) => t.group === group).map((topic) => (
                        <div
                          key={topic.id}
                          className="flex items-center justify-between gap-3 border-b border-melt-hair py-2 last:border-b-0"
                        >
                          <span className="text-[14px] text-melt-text1">{topic.name}</span>
                          <div className="w-[210px] shrink-0">
                            <Segmented
                              label={topic.name}
                              value={answers.ratings[topic.id] ?? "okay"}
                              onChange={(rating) => rate(topic.id, rating)}
                              options={RATINGS}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h1 className="text-[26px] font-semibold leading-[1.15] tracking-[-.02em]">We found these in your inbox</h1>
              <p className="mt-2 text-[14.5px] leading-[1.5] text-melt-text2">
                Your plan works toward each OA, giving it more of each day as it gets close. Prepping for one yourself? Leave it out.
              </p>
              <div className="scrollbar-thin -mr-3 mt-5 min-h-0 flex-1 overflow-y-auto pr-3">
                {oas.map((oa) => (
                  <div key={oa.id} className="flex items-center justify-between gap-3 border-b border-melt-hair py-3 last:border-b-0">
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-medium">{oa.company} OA</p>
                      <p className="mt-0.5 font-plex text-[11px] text-melt-text3">
                        {oa.dueKnown ? `Due ${dayLabel(oa.due)}` : `No date given · aiming for ${dayLabel(oa.due)}`}
                      </p>
                    </div>
                    <div className="w-[230px] shrink-0">
                      <Segmented
                        label={`${oa.company} OA`}
                        value={selfPrep.has(oa.id)}
                        onChange={(mine) =>
                          setSelfPrep((current) => {
                            const next = new Set(current);
                            if (mine) next.add(oa.id);
                            else next.delete(oa.id);
                            return next;
                          })
                        }
                        options={[
                          { value: false, label: "Plan prep" },
                          { value: true, label: "I’ll prep myself" },
                        ]}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="font-plex text-[13.5px] font-medium text-melt-text2 transition-colors hover:text-melt-text1"
              >
                Back
              </button>
            )}
            <span className="font-plex text-[12px] text-melt-text3">
              {step + 1} of {steps}
            </span>
          </div>
          <button
            type="button"
            disabled={!total || saving}
            onClick={() => (last ? save() : setStep(step + 1))}
            className="h-11 rounded-full bg-[#D08A3E] px-6 text-[14.5px] font-medium text-white transition-[background-color,opacity] duration-150 hover:bg-[#BF7A30] disabled:opacity-45 disabled:hover:bg-[#D08A3E]"
          >
            {last ? (saving ? "Saving…" : "Build my plan") : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
